import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { invalidateCache } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, ScoreBar, BandBadge, ScorePill } from "@/components/Shared";
import { DemoBadge } from "@/components/Brand";
import { AssessmentDayAdmin, CommsHub } from "@/components/CandidateComms";
import { WorkStyleProfile } from "@/components/WorkStyleProfile";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ClipboardEdit, Send, AlertTriangle, Trash2 } from "lucide-react";

const SCORE_LABELS = {
  coordination: "Coordination", spatial_awareness: "Spatial awareness", control_accuracy: "Control accuracy",
  instruction_following: "Instruction following", concentration: "Concentration", learning_rate: "Learning rate",
  response_to_correction: "Response to correction", safety_behaviour: "Safety behaviour",
  confidence_without_rushing: "Confidence without rushing",
  attitude: "Attitude", motivation: "Motivation", reliability: "Reliability", communication: "Communication",
  willingness_to_learn: "Willingness to learn", accepts_correction: "Accepts correction",
  safety_mindset: "Safety mindset", teamwork: "Teamwork", career_commitment: "Career commitment",
  travel_practicality: "Travel / work practicality", responsibility: "Responsibility",
  customer_facing_attitude: "Customer-facing attitude",
  listens_to_instructions: "Listens to instructions", remembers_instructions: "Remembers instructions",
  smoothness: "Smoothness", awareness_of_surroundings: "Awareness of surroundings",
  control_confidence: "Control confidence", does_not_rush: "Does not rush",
  stops_when_uncertain: "Stops when uncertain", responds_to_correction: "Responds positively to correction",
  machine_sympathy: "Machine sympathy", improvement_during_session: "Improvement during session",
  general_attitude: "General attitude", overall_aptitude: "Overall aptitude",
};

const SIM_RATING_LABELS = {
  strong: "Strong", suitable: "Suitable", requires_review: "Requires review", not_suitable: "Not suitable",
  recommended: "Recommended", further_assessment: "Further assessment", not_recommended: "Not recommended",
};

const PRACTICAL_FIELDS = [
  ["listens_to_instructions", "Listens to instructions"], ["remembers_instructions", "Remembers instructions"],
  ["coordination", "Coordination"], ["smoothness", "Smoothness"],
  ["spatial_awareness", "Spatial awareness"], ["awareness_of_surroundings", "Awareness of surroundings"],
  ["control_confidence", "Control confidence"], ["does_not_rush", "Does not rush"],
  ["stops_when_uncertain", "Stops when uncertain"], ["responds_to_correction", "Responds positively to correction"],
  ["machine_sympathy", "Machine sympathy"], ["improvement_during_session", "Improvement during session"],
  ["general_attitude", "General attitude"], ["overall_aptitude", "Overall aptitude"],
];

const DECISION_LABELS = {
  approve_trainee: "Admit to Training Academy",
  invite: "Invite to Assessment Day",
  decline: "Decline",
  hold: "Hold / Review",
  reinstate: "Reinstate",
};

const DECISION_STYLES = {
  approve_trainee: "bg-lomond text-black hover:bg-[#E6B400]",
  invite: "bg-lomond text-black hover:bg-[#E6B400]",
  decline: "bg-red-50 text-red-700 border border-red-300 hover:bg-red-100",
  hold: "bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200",
  reinstate: "bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200",
};

function stageDecisions(summary) {
  if (["trainee", "operator"].includes(summary.role)) return [];
  const s = summary.stage;
  if (s === "declined") return ["reinstate"];
  if (s === "invited") return ["decline"];
  if (summary.booking_status === "completed") return ["approve_trainee", "decline", "hold"];
  if (s === "assessment_completed" || s === "hold") return ["invite", "decline", "hold"];
  return ["decline"]; // awaiting_assessment
}

const LINKED_LABELS = {
  applicants: "Application profile (incl. work-style responses)",
  assessment_attempts: "Aptitude assessment attempts",
  simulator_assessments: "Simulator assessments",
  practical_assessments: "Practical assessments",
  interview_assessments: "Interview assessments",
  bookings: "Assessment bookings",
  communications: "Messages",
  training_progress: "Training records",
  action_plans: "Action plans",
  live_experience: "Live experience",
  live_assignments: "Live assignments",
  competence_reviews: "Competence reviews",
  external_training: "External training records",
  password_reset_tokens: "Password-reset records",
  onboarding_checklist: "Onboarding records",
};

const VALUE_LABELS = {
  very_much: "Very much", quite_a_lot: "Quite a lot", sometimes: "Sometimes",
  not_particularly: "Not particularly", comfortable: "Comfortable",
  probably_comfortable: "Probably comfortable", unsure: "Unsure", not_for_me: "Not for me",
  yes: "Yes", no: "No", usually: "Usually", probably: "Probably", learning: "Currently learning",
};

const PROFILE_LABELS = {
  date_of_birth: "Date of birth", mobile: "Mobile", home_address: "Home address", postcode: "Postcode",
  local_authority: "Local authority", driving_licence: "Full UK driving licence",
  access_to_transport: "Access to reliable transport", employment_status: "Currently", current_employer: "Current employer",
  work_history: "Previous work / experience", previous_sectors: "Previously worked in",
  earliest_availability: "Earliest availability",
  right_to_work: "Right to work", early_starts: "Early starts manageable",
  travel_to_sites: "Travel to training / sites", practical_work: "Enjoys practical, hands-on work",
  outdoors_comfort: "Working outdoors", long_term_career: "Long-term career opportunity",
  motivation: "What interests them about becoming a plant operator", heard_about: "Heard about via",
  referral_source: "Referral source", council_referral: "Council referral", funding_reference: "Funding reference",
};

// Fields from the pre-2026 application form — shown only when a legacy applicant has them
const LEGACY_LABELS = {
  construction_experience: "Construction experience (legacy)", plant_experience: "Plant experience (legacy)",
  qualifications_held: "Qualifications / cards (legacy)", licence_type: "Licence type (legacy)",
  preferred_geography: "Preferred geography (legacy)",
};

function profileValue(profile, k) {
  const v = profile[k];
  if (Array.isArray(v)) return v.length ? v.join(", ") : "";
  return VALUE_LABELS[v] || v || "";
}

export default function CandidateProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [delPreview, setDelPreview] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [delNotes, setDelNotes] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const isManagement = user?.role === "management_admin";
  const canUnlock = ["management_admin", "funding_admin"].includes(user?.role);

  const unlockApplication = async () => {
    try {
      await api.post(`/staff/candidates/${id}/unlock-application`);
      toast.success("Application unlocked for candidate editing.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = () => api.get(`/staff/candidates/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load candidate"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="candidate-loading" />;
  const { summary, profile, theory, simulator, practical, interview, weightings, work_style } = data;

  const submitDecision = async () => {
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    try {
      await api.post(`/staff/candidates/${id}/decision`, { decision, reason });
      toast.success("Decision recorded and audit-logged.");
      setDecision(null); setReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDelete = () => {
    setDelOpen(true); setDelPreview(null); setDelReason(""); setDelNotes("");
    api.get(`/admin/candidates/${id}/delete-preview`)
      .then((r) => setDelPreview(r.data))
      .catch((e) => { toast.error(apiError(e)); setDelOpen(false); });
  };

  const submitDelete = async (mode) => {
    if (!delReason) { toast.error("Select a reason."); return; }
    if (delReason === "other" && !delNotes.trim()) { toast.error("Notes are required for Other."); return; }
    setDelBusy(true);
    try {
      await api.delete(`/admin/candidates/${id}`, { data: { reason: delReason, notes: delNotes, mode } });
      toast.success(mode === "full" ? "Candidate and account deleted." : "Applicant profile removed — account preserved.");
      invalidateCache("/staff/candidates");
      if (mode === "full") navigate("/staff");
      else { setDelOpen(false); load(); }
    } catch (e) { toast.error(apiError(e)); } finally { setDelBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="candidate-profile-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/staff")} className="border-steel text-slate-600 bg-transparent hover:border-lomond" data-testid="back-to-candidates">
          <ArrowLeft className="h-4 w-4 mr-1" /> Candidates
        </Button>
        <Link to={`/staff/candidates/${id}/assess`}>
          <Button size="sm" className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="open-assessment-forms-btn">
            <ClipboardEdit className="h-4 w-4 mr-1.5" /> Assessment Forms
          </Button>
        </Link>
        {canUnlock && summary.role === "applicant" && profile.application_status === "submitted" && !profile.edit_unlocked && (
          <Button size="sm" variant="outline" onClick={unlockApplication}
            className="border-amber-400/40 text-amber-300 bg-transparent hover:border-amber-400" data-testid="unlock-application-btn">
            Unlock Application for Editing
          </Button>
        )}
        {profile.edit_unlocked && profile.application_status === "submitted" && (
          <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-amber-300 bg-amber-400/10 border-amber-400/30 self-center" data-testid="edit-unlocked-badge">
            Unlocked for candidate editing
          </Badge>
        )}
      </div>

      <div className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="candidate-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl" data-testid="candidate-name">{summary.name}</h1>
              {summary.is_demo && <DemoBadge />}
              <StatusPill stage={summary.role === "applicant" ? summary.stage : summary.role} label={summary.role === "applicant" ? summary.stage_label : summary.role.toUpperCase()} />
              {summary.role === "applicant" && (
                profile.application_status === "submitted"
                  ? <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-400/10 border-emerald-400/30" data-testid="application-status-badge">Application submitted</Badge>
                  : <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-slate-600 bg-slate-400/10 border-slate-400/30" data-testid="application-status-badge">Draft — {profile.completion_pct ?? 0}%</Badge>
              )}
              {profile.edit_requested && !profile.edit_unlocked && (
                <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-amber-300 bg-amber-400/10 border-amber-400/30" data-testid="edit-requested-badge">Edit requested</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">{summary.email} {profile.mobile ? `— ${profile.mobile}` : ""}</p>
            <p className="text-xs text-slate-500 font-mono mt-1">{profile.postcode || "No postcode"} — {profile.local_authority || "LA unknown"} — Funding: {summary.funding_potential}</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Assessment Day score</p>
            <p className="font-heading font-black text-4xl text-lomond" data-testid="overall-score">
              {summary.day_score != null ? `${summary.day_score}%` : "—"}
            </p>
            <p className="text-[10px] text-slate-500 font-mono">
              Interview {weightings?.interview ?? 40}% / Simulator {weightings?.simulator ?? 60}%
            </p>
            {summary.theory_score != null && (
              <p className="text-[10px] text-slate-500 font-mono mt-1" data-testid="header-theory-score">Online assessment: {summary.theory_score}%</p>
            )}
          </div>
        </div>

        {isManagement && summary.role === "applicant" && stageDecisions(summary).length > 0 && (
          <div className="mt-5 pt-5 border-t border-steel" data-testid="decision-buttons">
            {summary.stage === "assessment_completed" && (
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Do we want to meet this person at an assessment day?</p>
            )}
            <div className="flex flex-wrap gap-2">
              {stageDecisions(summary).map((d) => (
                <Button key={d} size="sm" className={`font-semibold ${DECISION_STYLES[d]}`} onClick={() => setDecision(d)} data-testid={`decision-${d}`}>
                  {DECISION_LABELS[d]}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {summary.management_review_required && (
        <div className="bg-red-50 border border-red-300 rounded-md p-4 flex items-start gap-3" data-testid="management-review-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-heading font-bold uppercase text-red-700">Management review required</p>
            <p className="text-sm text-red-600 mt-0.5">
              Serious safety / attitude concerns recorded{summary.red_flags?.length ? `: ${summary.red_flags.map((f) => f.replace(/_/g, " ")).join(", ")}` : ""}.
              Do not admit on numeric score alone.
            </p>
          </div>
        </div>
      )}

      <AssessmentDayAdmin candidateId={id} summary={summary} profile={profile} onChanged={load} />

      <WorkStyleProfile workStyle={work_style} />

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="theory-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Online Aptitude / Theory</h2>
          {theory ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="font-heading font-black text-3xl text-charcoal" data-testid="theory-score">{theory.score_pct}%</p>
                <BandBadge band={theory.recommendation} overridden={theory.safety_override} />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-obsidian rounded-sm p-3">
                  <p className="font-mono text-lg text-charcoal" data-testid="safety-errors">{theory.safety_critical_errors}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Safety-critical errors</p>
                </div>
                <div className="bg-obsidian rounded-sm p-3">
                  <p className="font-mono text-lg text-charcoal" data-testid="unanswered-count">{theory.unanswered}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Unanswered</p>
                </div>
                <div className="bg-obsidian rounded-sm p-3">
                  <p className="font-mono text-lg text-charcoal" data-testid="time-taken">{Math.round((theory.time_taken_seconds || 0) / 60)}m</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Time taken</p>
                </div>
              </div>
              <div className="space-y-3 pt-2">
                {Object.entries(theory.category_scores).map(([cat, pct]) => (
                  <ScoreBar key={cat} label={cat} pct={pct} testid={`cat-${cat.toLowerCase().replace(/[^a-z]/g, "-")}`} />
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="theory-not-taken">Online assessment not yet completed.</p>
          )}
        </section>

        <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="simulator-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Simulator Aptitude &amp; Control</h2>
          {simulator ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(simulator).filter(([k, v]) => SCORE_LABELS[k] && typeof v === "number").map(([k, v]) => (
                  <div key={k} className="flex justify-between bg-obsidian rounded-sm px-3 py-2">
                    <span className="text-xs text-slate-500">{SCORE_LABELS[k]}</span>
                    <ScorePill value={v} suffix="/5" testid={`sim-${k}`} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">
                {simulator.date} — {simulator.assessor} — {simulator.simulator_location}
              </p>
              {simulator.comments && <p className="text-sm text-slate-600 leading-relaxed border-l-2 border-lomond pl-3">{simulator.comments}</p>}
              <p className="text-sm">
                Final rating: <span className="font-semibold text-lomond uppercase font-mono text-xs tracking-widest" data-testid="sim-recommendation">{SIM_RATING_LABELS[simulator.recommendation] || simulator.recommendation?.replace(/_/g, " ")}</span>
              </p>
              {simulator.report_url && (
                <a href={fileUrl(simulator.report_url)} target="_blank" rel="noreferrer" className="text-xs text-lomond underline" data-testid="sim-report-link">
                  View scored simulator report
                </a>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="sim-not-done">Simulator assessment not yet recorded.</p>
          )}
        </section>

        <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="practical-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-1">Practical Aptitude Record</h2>
          <p className="text-xs text-slate-500 mb-4">Live-machine practical assessment takes place inside the 10-day Training Camp (daily practical Days 1–9 and the Day 10 Foundation Practical). Any pre-camp aptitude record is shown here for history only.</p>
          {practical ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {PRACTICAL_FIELDS.map(([k, label]) => (
                  <div key={k} className="flex justify-between bg-obsidian rounded-sm px-3 py-1.5">
                    <span className="text-xs text-slate-500">{label}</span>
                    <span className="font-mono text-xs font-bold text-charcoal">{practical.scores?.[k] ?? "—"}/5</span>
                  </div>
                ))}
              </div>
              <div className="bg-obsidian rounded-sm p-3 space-y-1">
                <p className="text-[10px] font-mono uppercase tracking-widest text-lomond">Learning rate</p>
                <p className="text-xs text-slate-600">Start: {practical.starting_ability || "—"}</p>
                <p className="text-xs text-slate-600">After instruction: {practical.ability_after_instruction || "—"}</p>
                <p className="text-xs text-slate-600">Speed of improvement: <span className="text-charcoal font-semibold">{practical.speed_of_improvement || "—"}</span></p>
              </div>
              {practical.comments && <p className="text-sm text-slate-600 border-l-2 border-lomond pl-3">{practical.comments}</p>}
              <p className="text-sm">Decision: <span className="font-semibold text-lomond uppercase font-mono text-xs tracking-widest" data-testid="practical-decision">{practical.decision?.replace(/_/g, " ")}</span></p>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="practical-not-done">Practical aptitude assessment not yet recorded.</p>
          )}
        </section>

        <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="interview-section">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Interview Scorecard</h2>
          {interview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(interview.scores || {}).filter(([, v]) => typeof v === "number").map(([k, v]) => (
                  <div key={k} className="flex justify-between bg-obsidian rounded-sm px-3 py-1.5">
                    <span className="text-xs text-slate-500">{SCORE_LABELS[k] || k.replace(/_/g, " ")}</span>
                    <span className="font-mono text-xs font-bold text-charcoal">{v}/5</span>
                  </div>
                ))}
              </div>
              {interview.reliability_travel_score != null && (
                <div className="flex justify-between bg-obsidian rounded-sm px-3 py-2">
                  <span className="text-xs text-slate-500">Reliability / travel review</span>
                  <span className="font-mono text-xs font-bold text-lomond" data-testid="reliability-score">{interview.reliability_travel_score}/5</span>
                </div>
              )}
              {(interview.red_flags?.length > 0 || interview.concerns?.length > 0) && (
                <div className="bg-red-50 border border-red-300 rounded-sm p-3" data-testid="interview-concerns">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-red-600 flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="h-3 w-3" /> Red flags — management review required
                  </p>
                  <ul className="text-xs text-red-600 space-y-0.5">
                    {(interview.red_flags || interview.concerns || []).map((c) => <li key={c}>— {c.replace(/_/g, " ")}</li>)}
                  </ul>
                </div>
              )}
              {interview.comments && <p className="text-sm text-slate-600 border-l-2 border-lomond pl-3">{interview.comments}</p>}
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="interview-not-done">Interview not yet recorded.</p>
          )}
        </section>
      </div>

      <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="application-details-section">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Application Summary</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          {Object.entries(PROFILE_LABELS).map(([k, label]) => (
            <div key={k}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</p>
              <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap" data-testid={`profile-field-${k}`}>{profileValue(profile, k) || "—"}</p>
            </div>
          ))}
        </div>
        {Object.keys(LEGACY_LABELS).some((k) => profile[k]) && (
          <div className="mt-6 pt-4 border-t border-steel" data-testid="legacy-fields-section">
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">Earlier application form answers (legacy)</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              {Object.entries(LEGACY_LABELS).filter(([k]) => profile[k]).map(([k, label]) => (
                <div key={k}>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</p>
                  <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap" data-testid={`profile-field-${k}`}>{profile[k]}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <CommsHub candidateId={id} summary={summary} profile={profile} />

      {isManagement && (
        <section className="border border-red-300 rounded-md p-5 bg-red-50/40" data-testid="danger-zone">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-700 mb-2">Danger zone</p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-slate-600 max-w-xl">Permanently remove this candidate and their records. Trainee/operator history and staff accounts are protected.</p>
            <Button variant="outline" onClick={openDelete} className="border-red-300 text-red-700 bg-white hover:bg-red-50 shrink-0" data-testid="delete-candidate-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete Candidate
            </Button>
          </div>
        </section>
      )}

      <Dialog open={delOpen} onOpenChange={() => setDelOpen(false)}>
        <DialogContent className="bg-white border-steel text-foreground max-w-lg" data-testid="delete-candidate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-red-700">Delete Candidate</DialogTitle>
          </DialogHeader>
          {!delPreview ? (
            <div className="py-6 flex justify-center"><div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="delete-preview-loading" /></div>
          ) : (
            <div className="space-y-4">
              <div className="bg-elevated border border-steel rounded-sm p-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="delete-candidate-facts">
                <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Name</p><p className="font-semibold text-charcoal" data-testid="del-cand-name">{delPreview.name}</p></div>
                <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Email</p><p className="text-charcoal break-all" data-testid="del-cand-email">{delPreview.email}</p></div>
                <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Current state</p><p className="text-charcoal" data-testid="del-cand-state">{delPreview.role === "applicant" ? delPreview.stage_label : delPreview.role.replace(/_/g, " ")}</p></div>
                <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Created</p><p className="text-charcoal" data-testid="del-cand-created">{String(delPreview.created_at || "").slice(0, 10) || "—"}</p></div>
                <div className="col-span-2"><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Assessment status</p><p className="text-charcoal" data-testid="del-cand-assessment">{delPreview.assessment_status}</p></div>
              </div>
              {delPreview.self ? (
                <p className="text-sm text-red-700 bg-red-50 border border-red-300 rounded-sm p-3" data-testid="delete-self-block">You cannot delete your own account.</p>
              ) : (
                <>
                  {delPreview.classification === "progressed" && (
                    <div className="bg-amber-50 border border-amber-300 rounded-sm p-3.5" data-testid="delete-progressed-block">
                      <p className="text-sm font-semibold text-amber-800">This person has progressed beyond Applicant and their training/employment history is protected.</p>
                      <p className="text-xs text-amber-800 mt-1">The account and all training, action-plan and competence history are preserved. You may remove the applicant-stage profile only, or escalate for a management review.</p>
                    </div>
                  )}
                  {delPreview.classification === "staff" && (
                    <div className="bg-amber-50 border border-amber-300 rounded-sm p-3.5" data-testid="delete-staff-block">
                      <p className="text-sm font-semibold text-amber-800">This account has a staff/admin role.</p>
                      <p className="text-xs text-amber-800 mt-1">The login, role and audit/security history are preserved. Only the applicant-stage profile can be removed here.</p>
                    </div>
                  )}
                  <div data-testid="delete-linked-records">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Linked records</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                      {Object.entries(LINKED_LABELS).map(([k, label]) => (
                        <div key={k} className="flex justify-between text-xs" data-testid={`linked-${k}`}>
                          <span className="text-slate-600">{label}</span>
                          <span className={`font-mono font-bold ${delPreview.linked[k] ? "text-red-700" : "text-slate-400"}`}>{delPreview.linked[k] ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Reason (required)</p>
                    <Select value={delReason} onValueChange={setDelReason}>
                      <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="del-cand-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test_demo" data-testid="del-cand-reason-test_demo">Test/demo account</SelectItem>
                        <SelectItem value="duplicate_registration" data-testid="del-cand-reason-duplicate_registration">Duplicate registration</SelectItem>
                        <SelectItem value="applicant_requested" data-testid="del-cand-reason-applicant_requested">Applicant requested deletion</SelectItem>
                        <SelectItem value="withdrawn" data-testid="del-cand-reason-withdrawn">Withdrawn application</SelectItem>
                        <SelectItem value="registration_error" data-testid="del-cand-reason-registration_error">Registration error</SelectItem>
                        <SelectItem value="other" data-testid="del-cand-reason-other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {delReason === "other" && (
                    <Input value={delNotes} onChange={(e) => setDelNotes(e.target.value)} placeholder="Notes (required for Other)"
                      className="bg-white border-steel text-charcoal" data-testid="del-cand-notes" />
                  )}
                </>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} className="border-steel text-charcoal bg-white" data-testid="del-cand-cancel-btn">Cancel</Button>
            {delPreview && !delPreview.self && delPreview.can_full_delete && (
              <Button onClick={() => submitDelete("full")} disabled={delBusy || !delReason}
                className="bg-red-600 text-white font-bold hover:bg-red-700" data-testid="del-cand-confirm-btn">
                {delBusy ? "Deleting..." : "Delete Candidate and Account"}
              </Button>
            )}
            {delPreview && !delPreview.self && !delPreview.can_full_delete && delPreview.can_profile_only && (
              <Button onClick={() => submitDelete("profile_only")} disabled={delBusy || !delReason}
                className="bg-red-600 text-white font-bold hover:bg-red-700" data-testid="del-cand-profile-only-btn">
                {delBusy ? "Removing..." : "Remove Applicant Profile Only"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!decision} onOpenChange={() => setDecision(null)}>
        <DialogContent className="bg-slate-panel border-steel text-foreground" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">
              {DECISION_LABELS[decision]} — {summary.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Management decisions require a reason. This is recorded in the audit log.</p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for this decision (required)"
            className="bg-obsidian border-steel text-charcoal min-h-24" data-testid="decision-reason-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDecision(null)} className="border-steel text-slate-700 bg-transparent" data-testid="decision-cancel-btn">Cancel</Button>
            <Button onClick={submitDecision} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="decision-confirm-btn">Confirm Decision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
